/**
 * What the Applicant agreed to, in the words they were shown. Approve Owl keeps each grant
 * against the exact text and links, so the text is read back from the rendered element rather
 * than kept as a second copy that could drift from what was on screen.
 */
export type ConsentGrant =
  | "approveowl_terms"
  | "dealership_terms"
  | "sms_conversational"
  | "sms_reminders"
  | "credit_check";

export interface ConsentLink {
  label: string;
  url: string;
}

export interface ConsentAcceptance {
  grants: ConsentGrant[];
  body: string;
  links: ConsentLink[];
  page_url?: string;
}

// The longest page URL Approve Owl accepts. A longer one would fail the whole request.
const MAX_PAGE_URL = 2048;

const squish = (text: string | null) =>
  (text ?? "").replace(/\s+/g, " ").trim();

export function readAcceptance(
  element: HTMLElement,
  grants: ConsentGrant[],
  pageUrl?: string,
): ConsentAcceptance {
  return {
    grants,
    body: squish(element.textContent),
    links: Array.from(element.querySelectorAll("a[href]"), (anchor) => ({
      label: squish(anchor.textContent),
      url: (anchor as HTMLAnchorElement).href,
    })),
    ...(pageUrl ? { page_url: pageUrl } : {}),
  };
}

/** What the consent text grants. The dealership's terms only count when the text links them. */
export function consentGrants(organization: {
  policy_links?: ConsentLink[];
}): ConsentGrant[] {
  return [
    "approveowl_terms",
    ...(organization.policy_links?.length
      ? (["dealership_terms"] as const)
      : []),
    "sms_conversational",
    "sms_reminders",
  ];
}

/** A host page worth recording: http(s), and short enough for Approve Owl to accept. */
export function recordablePageUrl(value: string): string | undefined {
  if (value.length > MAX_PAGE_URL) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:"
      ? value
      : undefined;
  } catch {
    return undefined;
  }
}

export const CREDIT_CHECK_CONSENT = "I consent to a credit check";

export const creditCheckAcceptance: ConsentAcceptance = {
  grants: ["credit_check"],
  body: CREDIT_CHECK_CONSENT,
  links: [],
};
